import { API_URL } from '@/config';
import React, { useState, useEffect } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';
import { Trash2, Plus, Camera, User } from 'lucide-react';
import { getImageUrl } from '@/utils/imageUrl';
import { formatDate } from '@/utils/dateFormat';

const API = `${API_URL}/api`;

const ProfilePage = () => {
  const { user: initialUser } = useOutletContext();
  const navigate = useNavigate();
  const [user, setUser] = useState(initialUser);
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [tab, setTab] = useState('personal');
  
  const [formData, setFormData] = useState({
    name: '', mobile: '', photo_url: '', date_of_birth: '', address: '',
    spouse_name: '', spouse_mobile: '', spouse_photo_url: '', spouse_date_of_birth: '',
    wedding_anniversary_date: '', joint_photo_url: '',
    professional_category: '', professional_title: '', company_name: '', office_address: '', business_card_url: ''
  });
  
  const [newChild, setNewChild] = useState({ name: '', gender: 'male', date_of_birth: '' });

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const [userRes, childrenRes] = await Promise.all([
        axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` } }),
        axios.get(`${API}/members/${initialUser.user_id}/children`, { headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` } })
      ]);
      setUser(userRes.data);
      setFormData({
        name: userRes.data.name || '',
        mobile: userRes.data.mobile || '',
        photo_url: userRes.data.photo_url || '',
        date_of_birth: userRes.data.date_of_birth?.split('T')[0] || '',
        address: userRes.data.address || '',
        spouse_name: userRes.data.spouse_name || '',
        spouse_mobile: userRes.data.spouse_mobile || '',
        spouse_photo_url: userRes.data.spouse_photo_url || '',
        spouse_date_of_birth: userRes.data.spouse_date_of_birth?.split('T')[0] || '',
        wedding_anniversary_date: userRes.data.wedding_anniversary_date?.split('T')[0] || '',
        joint_photo_url: userRes.data.joint_photo_url || '',
        professional_category: userRes.data.professional_category || '',
        professional_title: userRes.data.professional_title || '',
        company_name: userRes.data.company_name || '',
        office_address: userRes.data.office_address || '',
        business_card_url: userRes.data.business_card_url || ''
      });
      setChildren(childrenRes.data);
    } catch (error) {
      console.error('Error loading profile:', error);
      toast.error('Failed to load profile');
    }
  };

  const handleFileUpload = async (file, fieldName) => {
    if (!file) return;
    
    setUploading(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      
      const response = await axios.post(`${API}/upload`, uploadFormData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("session_token")}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setFormData(prev => ({ ...prev, [fieldName]: response.data.url }));
      toast.success('Photo uploaded! Click "Save Changes" to apply.');
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/members/${user.user_id}`, formData, { headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` } });
      toast.success('Profile updated successfully!');
      loadProfile();
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleAddChild = async () => {
    if (!newChild.name || !newChild.date_of_birth) {
      toast.error('Name and date of birth are required');
      return;
    }
    try {
      await axios.post(`${API}/members/${user.user_id}/children`, newChild, { headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` } });
      toast.success('Child added successfully!');
      setNewChild({ name: '', gender: 'male', date_of_birth: '' });
      loadProfile();
    } catch (error) {
      toast.error('Failed to add child');
    }
  };

  const handleDeleteChild = async (childId) => {
    try {
      await axios.delete(`${API}/children/${childId}`, { headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` } });
      toast.success('Child removed');
      loadProfile();
    } catch (error) {
      toast.error('Failed to delete child');
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` } });
      toast.success('Logged out');
      navigate('/', { replace: true });
    } catch (error) {
      navigate('/', { replace: true });
    }
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-4xl" data-testid="profile-page">
      <div>
        <h1 className="text-4xl font-bold mb-2" style={{ color: '#0F172A' }}>My Profile</h1>
        <p className="text-muted-foreground">Update your information</p>
      </div>
      
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="personal" data-testid="personal-tab">Personal</TabsTrigger>
          <TabsTrigger value="family" data-testid="family-tab">Family</TabsTrigger>
          <TabsTrigger value="professional" data-testid="professional-tab">Professional</TabsTrigger>
        </TabsList>
        
        <TabsContent value="personal">
          <Card>
            <CardContent className="p-6 space-y-4">
              <div>
                <Label>Name *</Label>
                <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} data-testid="name-input" />
              </div>
              <div>
                <Label>Mobile *</Label>
                <Input value={formData.mobile} onChange={(e) => setFormData({...formData, mobile: e.target.value})} data-testid="mobile-input" />
              </div>
              <div>
                <Label>Profile Photo</Label>
                <div className="flex items-center gap-4 mt-2">
                  <div className="relative w-24 h-24 rounded-full overflow-hidden bg-slate-100 border-2 flex items-center justify-center shrink-0" style={{ borderColor: '#D4AF37' }}>
                    {formData.photo_url ? (
                      <img src={getImageUrl(formData.photo_url)} alt="Profile" className="w-full h-full object-cover" data-testid="profile-photo-preview" />
                    ) : (
                      <User className="w-10 h-10 text-slate-400" />
                    )}
                  </div>
                  <div>
                    <label
                      htmlFor="profile-photo-input"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer text-sm font-medium transition-colors hover:opacity-90"
                      style={{ backgroundColor: '#0F172A', color: '#fff', pointerEvents: uploading ? 'none' : 'auto', opacity: uploading ? 0.6 : 1 }}
                      data-testid="change-photo-btn"
                    >
                      <Camera className="w-4 h-4" />
                      {formData.photo_url ? 'Change Photo' : 'Upload Photo'}
                    </label>
                    <input
                      id="profile-photo-input"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleFileUpload(e.target.files[0], 'photo_url')}
                      disabled={uploading}
                      data-testid="photo-upload"
                    />
                    <p className="text-xs text-muted-foreground mt-2">
                      {uploading ? 'Uploading…' : 'JPG or PNG. Remember to click Save Changes.'}
                    </p>
                  </div>
                </div>
              </div>
              <div>
                <Label>Date of Birth</Label>
                <Input type="date" value={formData.date_of_birth} onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})} data-testid="dob-input" />
              </div>
              <div>
                <Label>Residential Address</Label>
                <Input value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} placeholder="Full address" data-testid="address-input" />
              </div>
              <Button onClick={handleSave} disabled={loading || uploading} data-testid="save-personal-btn">
                {uploading ? 'Uploading...' : (loading ? 'Saving...' : 'Save Changes')}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="family">
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Spouse Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Spouse Name</Label>
                <Input value={formData.spouse_name} onChange={(e) => setFormData({...formData, spouse_name: e.target.value})} />
              </div>
              <div>
                <Label>Spouse Mobile</Label>
                <Input value={formData.spouse_mobile} onChange={(e) => setFormData({...formData, spouse_mobile: e.target.value})} />
              </div>
              <div>
                <Label>Spouse Date of Birth</Label>
                <Input type="date" value={formData.spouse_date_of_birth} onChange={(e) => setFormData({...formData, spouse_date_of_birth: e.target.value})} />
              </div>
              <div>
                <Label>Wedding Anniversary</Label>
                <Input type="date" value={formData.wedding_anniversary_date} onChange={(e) => setFormData({...formData, wedding_anniversary_date: e.target.value})} />
              </div>
              <div>
                <Label>Spouse Photo</Label>
                <Input type="file" accept="image/*" onChange={(e) => handleFileUpload(e.target.files[0], 'spouse_photo_url')} disabled={uploading} />
                {formData.spouse_photo_url && <p className="text-xs text-muted-foreground mt-1">Current photo uploaded</p>}
              </div>
              <div>
                <Label>Joint Photo</Label>
                <Input type="file" accept="image/*" onChange={(e) => handleFileUpload(e.target.files[0], 'joint_photo_url')} disabled={uploading} />
                {formData.joint_photo_url && <p className="text-xs text-muted-foreground mt-1">Current photo uploaded</p>}
              </div>
              <Button onClick={handleSave} disabled={loading || uploading}>
                {uploading ? 'Uploading...' : (loading ? 'Saving...' : 'Save Changes')}
              </Button>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>Children</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {children.map((child) => (
                <div key={child.child_id} className="flex items-center gap-4 p-4 border rounded-lg">
                  <div className="flex-1">
                    <p className="font-medium">{child.name}</p>
                    <p className="text-sm text-muted-foreground capitalize">{child.gender}</p>
                    <p className="text-sm text-muted-foreground">{formatDate(child.date_of_birth)}</p>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => handleDeleteChild(child.child_id)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              
              <div className="border-t pt-4 space-y-4">
                <h4 className="font-medium">Add Child</h4>
                <Input placeholder="Name" value={newChild.name} onChange={(e) => setNewChild({...newChild, name: e.target.value})} data-testid="new-child-name" />
                <Select value={newChild.gender} onValueChange={(v) => setNewChild({...newChild, gender: v})}>
                  <SelectTrigger data-testid="new-child-gender">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                  </SelectContent>
                </Select>
                <Input type="date" value={newChild.date_of_birth} onChange={(e) => setNewChild({...newChild, date_of_birth: e.target.value})} data-testid="new-child-dob" />
                <Button onClick={handleAddChild} data-testid="add-child-btn">
                  <Plus className="w-4 h-4 mr-2" /> Add Child
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="professional">
          <Card>
            <CardContent className="p-6 space-y-4">
              <div>
                <Label>Professional Category</Label>
                <Input value={formData.professional_category} onChange={(e) => setFormData({...formData, professional_category: e.target.value})} />
              </div>
              <div>
                <Label>Professional Title</Label>
                <Input value={formData.professional_title} onChange={(e) => setFormData({...formData, professional_title: e.target.value})} />
              </div>
              <div>
                <Label>Company Name</Label>
                <Input value={formData.company_name} onChange={(e) => setFormData({...formData, company_name: e.target.value})} />
              </div>
              <div>
                <Label>Office/Business Address</Label>
                <Input value={formData.office_address} onChange={(e) => setFormData({...formData, office_address: e.target.value})} placeholder="Office address" data-testid="office-address-input" />
              </div>
              <div>
                <Label>Business Card</Label>
                <Input type="file" accept="image/*,.pdf" onChange={(e) => handleFileUpload(e.target.files[0], 'business_card_url')} disabled={uploading} />
                {formData.business_card_url && <p className="text-xs text-muted-foreground mt-1">Current card uploaded</p>}
              </div>
              <Button onClick={handleSave} disabled={loading || uploading}>
                {uploading ? 'Uploading...' : (loading ? 'Saving...' : 'Save Changes')}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      <Card className="border-destructive">
        <CardContent className="p-6">
          <h3 className="font-semibold mb-2 text-destructive">Logout</h3>
          <p className="text-sm text-muted-foreground mb-4">End your current session</p>
          <Button variant="destructive" onClick={handleLogout} data-testid="profile-logout-btn">Logout</Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProfilePage;
